// eslint-disable-next-line import/no-named-as-default
import CSSReset from '@chakra-ui/css-reset';
import { PortalManager } from '@chakra-ui/portal';
import {
  ColorModeProvider,
  ColorModeProviderProps,
  GlobalStyle,
  ThemeProvider,
  ThemeProviderProps,
} from '@chakra-ui/system';
import { Dict } from '@chakra-ui/utils';
import { theme as defaultTheme, Theme } from '@lego/klik-ui-theme';
import * as React from 'react';

export interface IKlikProviderProps extends Pick<ThemeProviderProps, 'cssVarsRoot'> {
  /**
   * a theme. if omitted, uses the default theme provided by klik
   */
  theme?: Dict | Theme;
  /**
   * Common z-index to use for `Portal`
   *
   * @default undefined
   */
  portalZIndex?: number;
  /**
   * If `true`, `CSSReset` component will be mounted to help
   * you reset browser styles
   *
   * @default true
   */
  resetCSS?: boolean;
  /**
   * manager to persist a users color mode preference in
   *
   * omit if you don't render server-side
   * for SSR: choose `cookieStorageManager`
   *
   * @default localStorageManager
   */
  colorModeManager?: ColorModeProviderProps['colorModeManager'];
  children?: React.ReactNode;
  /**
   * Auto-inject Font
   *
   * If `true` a <link rel="stylesheet"> will be added to the document head,
   * pointing to the font stylesheet from our CDN
   * This is currently a opt-in, and defaults to false
   *
   * @default false
   */
  includeFont?: boolean;
}

/**
 * The global provider that must be added to make all Klik components work correctly
 */
export const KlikProvider: React.FunctionComponent<IKlikProviderProps> = ({
  children,
  colorModeManager,
  portalZIndex,
  resetCSS: shouldResetCSS = true,
  theme = defaultTheme,
  includeFont = false,
  cssVarsRoot,
}) => {
  React.useEffect(() => {
    if (!includeFont) {
      return;
    }

    const id = 'klik-fonts-cdn';

    if (!document.head.querySelector(`#${id}`)) {
      const link = document.createElement('link');
      link.id = id;
      link.href = 'https://assets.lego.com/fonts/v1/cera-pro.css';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
  }, [includeFont]);

  return (
    <ThemeProvider cssVarsRoot={cssVarsRoot} theme={theme}>
      <ColorModeProvider colorModeManager={colorModeManager} options={theme.config}>
        {shouldResetCSS && <CSSReset />}
        <GlobalStyle />
        {portalZIndex ? <PortalManager zIndex={portalZIndex}>{children}</PortalManager> : children}
      </ColorModeProvider>
    </ThemeProvider>
  );
};
